import {observer} from "mobx-react-lite";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {IconSource} from "../../../shared/enums/iconSource";
import {getTranslationCodeForError} from "../../../stores/domainStore/pages/models/accountCreationErrorCode";
import {ResidentAccountDetail} from "../../../stores/domainStore/pages/models/residentAccountDetail";
import ResidentModalDetail from "../../../stores/domainStore/pages/models/residentModalDetail";
import ButtonIcon from "../../atoms/button/buttonIcon";
import NavigationButton from "../../atoms/button/navigationButton";
import RadioGroup from "../../atoms/radioGroup/radioGroup";
import EditLabel from "../../molecules/editLabel/editLabel";
import ModalDetailContent from "../../molecules/modalDetailContent/modalDetailContent";
import "./residentDetailModal.sass";
import AppAccessStatusTab from "../../molecules/appAccessStatusTab/appAccessStatusTab";
import {AppAccessStatus} from "../../../shared/enums/appAccessStatus";
import IneligibleDisplay from "../../molecules/ineligibleDisplay/ineligibleDisplay";

export interface IResidentDetailModalProps {
  resident?: ResidentModalDetail;
  residentAccountDetails?: ResidentAccountDetail;
  isLoading: boolean;
  isOpen: boolean;
  createAccount?: () => void;
  removeAccountStart?: () => void;
  editEmail?: () => void;
  clearSelectedResident?: () => void;
  clearErrorMessage?: () => void;
  resetPassword?: () => void;
  isResetPasswordSuccessModalOpen?: boolean;
  closeResetPasswordSuccessModal?: () => void;
  namespace?: string;
}

const ResidentDetailModal: React.FC<IResidentDetailModalProps> = ({
  resident,
  residentAccountDetails,
  isLoading,
  isOpen,
  createAccount,
  removeAccountStart,
  editEmail,
  clearSelectedResident,
  clearErrorMessage,
  resetPassword,
  isResetPasswordSuccessModalOpen,
  closeResetPasswordSuccessModal,
  namespace = "residentadmin"
}) => {
  const {t} = useTranslation(["translation", "residentadmin"]);
  const [copied, setCopied] = useState(false);

  const formatHeaderText = (): string => {
    if (resident) {
      return resident.formattedResidentName;
    }
    // Should never happen, but provides coverage
    return t("residentadmin:labels.residentDetails");
  };

  const formatErrorMessage = (): string => {
    if (!residentAccountDetails) {
      // Only show radio group error if we don't have active account
      if (!resident?.hasActiveAccount && resident?.radioGroupHasError) {
        return t("residentadmin:errors.emptyEmailAddress");
      }
      // Only show edit email error if we have active account
      if (resident?.hasActiveAccount && resident?.editEmailHasError) {
        return t("residentadmin:errors.emptyEmailAddress");
      }
      return "";
    }

    // Ensure create account details has an error to show
    if (!residentAccountDetails.error && !residentAccountDetails.errorCode) {
      return "";
    }

    if (residentAccountDetails.errorCode) {
      return t(getTranslationCodeForError(residentAccountDetails.errorCode));
    }

    return residentAccountDetails.error ?? t("residentadmin:errors.unknown");
  };

  const errorMessage = formatErrorMessage();
  const allowForAccountCreation =
    !resident?.hasActiveAccount && (errorMessage || !resident?.password);
  const displayPhoneNumber = resident?.mobileNumber || resident?.phoneNumber;

  return (
    <>
      <Modal
        isOpen={isOpen}
        modalClassName="theme-modal resident-detail-modal"
        contentClassName="border"
        centered
      >
        <ModalHeader
          close={
            <NavigationButton
              icon={IconSource.exit}
              onClick={clearSelectedResident}
            />
          }
        >
          {formatHeaderText()}
        </ModalHeader>
        <ModalBody>
          {resident &&
            resident.appAccessStatus === AppAccessStatus.RequiresAction && (
              <IneligibleDisplay namespace={namespace} />
            )}

          {resident && (
            <>
              <div className="resident-details-modal-section">
                <div>
                  <span>{t("residentadmin:labels.residentDetails")}</span>
                </div>
                {displayPhoneNumber && (
                  <ModalDetailContent
                    icon={IconSource.phone}
                    content={displayPhoneNumber}
                  />
                )}
                {resident.unitCode && (
                  <ModalDetailContent
                    icon={IconSource.home}
                    content={resident.unitCode}
                  />
                )}
              </div>
              <hr />
              <div className="resident-details-modal-section">
                <div>
                  <span className="section=title">App Account Details</span>
                  <AppAccessStatusTab
                    appAccessStatus={resident.appAccessStatus}
                  />
                </div>
                {allowForAccountCreation ? (
                  <>
                    <div>
                      <RadioGroup
                        items={resident.radioGroupItems}
                        name="AppAccountDetails"
                        isDisabled={isLoading}
                        onSelect={clearErrorMessage}
                      />
                    </div>
                    {errorMessage && (
                      <span className="error">{errorMessage}</span>
                    )}
                  </>
                ) : (
                  <>
                    <ModalDetailContent icon={IconSource.email}>
                      <EditLabel
                        isEditing={resident.isEditingActiveResident}
                        text={resident.emailTextObservable}
                        isDisabled={isLoading}
                      />
                    </ModalDetailContent>
                    {resident.hasActiveAccount &&
                      !resident.isEditingActiveResident.value &&
                      !resident?.password && (
                        <div className="reset-password-inline mb-2">
                          <ButtonIcon
                            type="secondary"
                            onClick={resetPassword}
                            isDisabled={isLoading}
                          >
                            {t("residentadmin:buttons.resetPassword")}
                          </ButtonIcon>
                        </div>
                      )}
                    {errorMessage && (
                      <span className="error">{errorMessage}</span>
                    )}
                    {resident?.password && (
                      <>
                        <ModalDetailContent icon={IconSource.password}>
                          <span className="temporary-password">
                            {resident.password}
                          </span>
                        </ModalDetailContent>
                        <span className="password-help-text">
                          {t("residentadmin:labels.passwordHelperText.lineOne")}
                          <br />
                          {t("residentadmin:labels.passwordHelperText.lineTwo")}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </ModalBody>
        {allowForAccountCreation && (
          <ModalFooter className="modal-footer-button bg-light">
            <ButtonIcon
              type="primary"
              onClick={createAccount}
              isDisabled={resident?.createAccountDisabled}
              isBusy={isLoading}
            >
              {t("residentadmin:buttons.createAccount")}
            </ButtonIcon>
          </ModalFooter>
        )}
        {resident &&
          resident.appAccessStatus === AppAccessStatus.RequiresAction && (
            <ModalFooter className="modal-footer-remove-account-start bg-light">
              <ButtonIcon
                type="primary-negative"
                onClick={removeAccountStart}
                isDisabled={resident?.createAccountDisabled}
                isBusy={isLoading}
              >
                {t("residentadmin:buttons.removeAccount")}
              </ButtonIcon>
            </ModalFooter>
          )}
        {resident?.isEditingActiveResident.value && (
          <ModalFooter className="modal-footer-button bg-light">
            <ButtonIcon
              type="primary"
              onClick={editEmail}
              isDisabled={resident?.editEmailDisabled}
              isBusy={isLoading}
            >
              {t("translation:actions.save")}
            </ButtonIcon>
            <ButtonIcon
              type="secondary"
              onClick={resident.resetEditEmail}
              isDisabled={isLoading}
            >
              {t("translation:actions.cancel")}
            </ButtonIcon>
          </ModalFooter>
        )}
      </Modal>
      <Modal
        isOpen={isResetPasswordSuccessModalOpen}
        modalClassName="theme-modal resident-detail-modal"
        contentClassName="border"
        centered
      >
        <ModalHeader
          close={
            <NavigationButton
              icon={IconSource.exit}
              onClick={closeResetPasswordSuccessModal}
            />
          }
        >
          {t("residentadmin:labels.resetPasswordSuccessTitle")}
        </ModalHeader>
        <ModalBody>
          <div className="resident-details-modal-section">
            <ModalDetailContent icon={IconSource.email}>
              <span>{resident?.emailTextObservable.text}</span>
            </ModalDetailContent>
            <ModalDetailContent icon={IconSource.password}>
              <span className="temporary-password">
                {resident?.tempPassword}
              </span>
            </ModalDetailContent>
            <span className="password-help-text">
              {t("residentadmin:labels.passwordResetHelperText.lineOne")}
              <br />
              {t("residentadmin:labels.passwordResetHelperText.lineTwo")}
            </span>
          </div>
        </ModalBody>
        <ModalFooter className="modal-footer-button bg-light">
          <ButtonIcon
            type="primary"
            onClick={() => {
              if (resident?.tempPassword) {
                navigator.clipboard.writeText(resident.tempPassword);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }
            }}
          >
            {copied
              ? t("residentadmin:buttons.passwordCopied")
              : t("residentadmin:buttons.passwordCopy")}
          </ButtonIcon>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default observer(ResidentDetailModal);
